import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'

import { resolveStepBranchTarget } from './stepBranching'
import type { LibraWorkflowStep } from './workflowStepsApi'

type BranchingStep = Pick<LibraWorkflowStep, 'id' | 'branch'>

/**
 * Each step a branch with `deactivateSkippedSteps` jumps over for these field
 * values, mapped to the id of the branching step that skips it. A branch that
 * is itself inactive does not apply, and an unresolved decision or a backward
 * jump skips nothing.
 */
export function getSkippingStepIds(
  workflow: { steps: ReadonlyArray<BranchingStep> },
  fieldValues: FieldValues
): ReadonlyMap<string, string> {
  const { steps } = workflow
  const indices = new Map(steps.map((step, index) => [step.id, index]))
  const skippedBy = new Map<string, string>()

  steps.forEach((step, index) => {
    if (skippedBy.has(step.id) || !step.branch?.deactivateSkippedSteps) return
    const target = resolveStepBranchTarget(step.branch, fieldValues)
    const targetIndex = target ? indices.get(target.goToStepId) : undefined
    if (targetIndex === undefined || targetIndex <= index + 1) return
    steps.slice(index + 1, targetIndex).forEach((skipped) => {
      skippedBy.set(skipped.id, step.id)
    })
  })

  return skippedBy
}

export function getInactiveStepIds(
  workflow: { steps: ReadonlyArray<BranchingStep> },
  fieldValues: FieldValues
): ReadonlySet<string> {
  return new Set(getSkippingStepIds(workflow, fieldValues).keys())
}
